import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AppContext';
import { useIsMobile } from '../hooks/useIsMobile';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Search, RefreshCw, Megaphone, GitBranch, Download, KeyRound, Link2, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ApiKeysModal from '../components/ApiKeysModal';
import ClickPartnerMappingModal from '../components/ClickPartnerMappingModal';
import TapfiliateConfigModal from '../components/TapfiliateConfigModal';

function intakeTypeLabel(t) {
  if (t === 'click') return 'Click';
  if (t === 'affiliate_clicks') return 'Affiliate Clicks';
  return 'API';
}

function CampaignModal({ campaign, onClose, onSaved }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState(
    campaign
      ? {
          name: campaign.name || '',
          affiliate_profile_id: campaign.affiliate_profile_id || campaign.affiliate_profile?.id || '',
          vertical: campaign.vertical || '',
          intake_type: campaign.intake_type || 'api',
          payout_type: campaign.payout_type || 'rev_share',
          payout_rate: campaign.payout_rate || '',
          payout_amount: campaign.payout_amount || '',
          minimum_payout: campaign.minimum_payout || '',
          fallback_redirect_url: campaign.fallback_redirect_url || '',
          is_active: campaign.is_active !== false,
        }
      : {
          name: '',
          affiliate_profile_id: '',
          vertical: '',
          intake_type: 'api',
          payout_type: 'rev_share',
          payout_rate: '',
          payout_amount: '',
          minimum_payout: '',
          fallback_redirect_url: '',
          is_active: true,
        }
  );
  const [saving, setSaving]         = useState(false);
  const [partners, setPartners]     = useState([]);
  const [verticals, setVerticals]   = useState([]);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/affiliates', { params: { page: 1, limit: 100 } })
      .then(res => setPartners(Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.data || [])))
      .catch(() => setPartners([]));
    api.get('/config/verticals')
      .then(res => setVerticals(Array.isArray(res.data) ? res.data : []))
      .catch(() => setVerticals([]));
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.affiliate_profile_id) {
      toast.error('Select an affiliate for this campaign');
      return;
    }
    if (form.payout_type === 'fixed_static' && form.fallback_redirect_url) {
      const url = form.fallback_redirect_url.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        toast.error('Fallback URL must start with http:// or https://');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        affiliate_profile_id: Number(form.affiliate_profile_id),
        vertical: form.vertical.trim() || undefined,
        intake_type: form.intake_type,
        payout_type: form.payout_type,
        payout_rate: form.payout_rate !== '' ? String(form.payout_rate) : null,
        payout_amount: form.payout_amount !== '' ? String(form.payout_amount) : null,
        minimum_payout: form.minimum_payout !== '' ? String(form.minimum_payout) : null,
        fallback_redirect_url: form.fallback_redirect_url?.trim() || '',
        is_active: form.is_active,
      };

      if (campaign) {
        await api.put(`/campaigns/${campaign.id || campaign._id}`, payload);
        toast.success('Campaign updated');
      } else {
        await api.post('/campaigns/', payload);
        toast.success('Campaign created');
      }
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || err.message || 'Error saving campaign';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{campaign ? 'Edit Campaign' : 'New Campaign'}</h2>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="grid-2" style={isMobile ? { gridTemplateColumns: '1fr' } : undefined}>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="input" value={form.name}
                  onChange={e => f('name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Affiliate</label>
                <select className="select" value={form.affiliate_profile_id}
                  onChange={e => f('affiliate_profile_id', e.target.value)} required>
                  <option value="">— Select Affiliate —</option>
                  {partners.map(p => (
                    <option key={p.id || p.aff_id} value={p.id || p.aff_id}>
                      {p.company_name ? `${p.company_name} (${p.aff_id})` : (p.name || p.aff_id)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Vertical</label>
                <select className="select" value={form.vertical} onChange={e => f('vertical', e.target.value)}>
                  <option value="">— Select Vertical —</option>
                  {verticals.map(v => (
                    <option key={v.identifier} value={v.identifier}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Intake Type</label>
                <select className="select" value={form.intake_type}
                  onChange={e => f('intake_type', e.target.value)}>
                  <option value="api">API (affiliate posts directly to our intake API)</option>
                  <option value="click">Click (affiliate sends traffic to our web form)</option>
                  <option value="affiliate_clicks">Affiliate Clicks (traffic from Tapfiliate or similar network)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="select" value={String(form.is_active)}
                  onChange={e => f('is_active', e.target.value === 'true')}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div className="form-group" style={{ gridColumn: isMobile ? undefined : '1 / -1' }}>
                <label className="form-label">Payout Type</label>
                <select className="select" value={form.payout_type}
                  onChange={e => f('payout_type', e.target.value)}>
                  <option value="rev_share">Revenue Share (% of sale)</option>
                  <option value="fixed_accepted">Fixed (Only if accepted by buyer)</option>
                  <option value="fixed_static">Fixed (Every submitted lead)</option>
                </select>
              </div>

              {form.payout_type === 'rev_share' && (
                <div className="form-group">
                  <label className="form-label">Payout Rate (%)</label>
                  <input className="input" type="number" step="0.0001" value={form.payout_rate}
                    onChange={e => f('payout_rate', e.target.value)} placeholder="0.30" />
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.4 }}>
                    Fraction of revenue paid (e.g. 0.30 = 30%). Unsold leads return accepted=False.
                  </div>
                </div>
              )}

              {(form.payout_type === 'fixed_accepted' || form.payout_type === 'fixed_static') && (
                <div className="form-group">
                  <label className="form-label">Payout Amount ($)</label>
                  <input className="input" type="number" step="0.01" value={form.payout_amount}
                    onChange={e => f('payout_amount', e.target.value)} placeholder="15.00" />
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.4 }}>
                    {form.payout_type === 'fixed_accepted'
                      ? 'Flat amount paid per lead only when accepted by a buyer.'
                      : 'Flat amount billed on every lead. Unsold leads return accepted=True + Fallback Offerwall URL.'}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Minimum Payout ($)</label>
                <input className="input" type="number" step="0.01" value={form.minimum_payout}
                  onChange={e => f('minimum_payout', e.target.value)} placeholder="5.00" />
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.4 }}>
                  Minimum payout floor. Buyers offering less are excluded.
                </div>
              </div>

              {form.payout_type === 'fixed_static' && (
                <div className="form-group" style={{ gridColumn: isMobile ? undefined : '1 / -1' }}>
                  <label className="form-label">Fallback Offerwall URL</label>
                  <input className="input" type="url" value={form.fallback_redirect_url}
                    onChange={e => f('fallback_redirect_url', e.target.value)} placeholder="https://offerwall.example.com/redirect" />
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.4 }}>
                    Redirect URL returned when lead is unsold on fixed_static campaigns. Must be http:// or https://.
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? <div className="spinner" /> : (campaign ? 'Save changes' : 'Create campaign')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CampaignCard({ c, isAdmin, onEdit, onEditFlow, onDownloadSpec, onKeys, onPartnerMapping, onTapfiliate, onDelete }) {
  return (
    <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', background: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--accent-fg)', flexShrink: 0,
          }}>
            <Megaphone size={16} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{c.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.vertical || 'No vertical'}</div>
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {c.intake_type === 'api' && (
              <button className="btn btn-ghost btn-icon btn-sm" title="Download API Spec (PDF)" onClick={(e) => { e.stopPropagation(); onDownloadSpec(); }}><Download size={13} /></button>
            )}
            <button className="btn btn-ghost btn-icon btn-sm" title="Edit Flow" onClick={(e) => { e.stopPropagation(); onEditFlow(); }}><GitBranch size={13} /></button>
            <button className="btn btn-ghost btn-icon btn-sm" title="API Keys" onClick={(e) => { e.stopPropagation(); onKeys(); }}><KeyRound size={13} /></button>
            {(c.intake_type === 'click' || c.intake_type === 'affiliate_clicks') && (
              <button className="btn btn-ghost btn-icon btn-sm" title="Click Partner Mapping" onClick={(e) => { e.stopPropagation(); onPartnerMapping(); }}><Link2 size={13} /></button>
            )}
            {c.intake_type === 'affiliate_clicks' && (
              <button className="btn btn-ghost btn-icon btn-sm" title="Tapfiliate Config" onClick={(e) => { e.stopPropagation(); onTapfiliate(); }}><Share2 size={13} /></button>
            )}
            <button className="btn btn-ghost btn-icon btn-sm" onClick={(e) => { e.stopPropagation(); onEdit(); }}><Pencil size={13} /></button>
            <button className="btn btn-danger btn-icon btn-sm" onClick={(e) => { e.stopPropagation(); onDelete(); }}><Trash2 size={13} /></button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <span className="badge badge-gray">{intakeTypeLabel(c.intake_type)}</span>
        <span className="badge badge-gray">{c.payout_type}</span>
        <span className={`badge ${c.is_active !== false ? 'badge-green' : 'badge-red'}`}>
          {c.is_active !== false ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 10, borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontWeight: 600, flexShrink: 0 }}>Affiliate</span>
          <span style={{ textAlign: 'right', wordBreak: 'break-word' }}>{c.affiliate_profile?.name || c.affiliate_profile_id || '—'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontWeight: 600, flexShrink: 0 }}>Created</span>
          <span>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</span>
        </div>
      </div>
    </div>
  );
}

export default function CampaignsPage() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [data, setData]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [q, setQ]             = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(null);
  const [delId, setDelId]     = useState(null);
  const [keysFor, setKeysFor] = useState(null);
  const [partnerMappingFor, setPartnerMappingFor] = useState(null);
  const [tapfiliateFor, setTapfiliateFor] = useState(null);

  const fetchData = useCallback(async (search = '') => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/campaigns', {
        params: { limit: 100, ...(search ? { q: search } : {}) },
      });
      setData(Array.isArray(res) ? res : (res.items || res.data || []));
      setTotal(res.count ?? res.total ?? (Array.isArray(res) ? res.length : 0));
    } catch { toast.error('Error loading campaigns'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async id => {
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success('Campaign deleted');
      fetchData(q);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error deleting'); }
    setDelId(null);
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Campaigns</h1>
        <p>{total} campaigns · <code style={{ fontSize: 12 }}>GET /campaigns</code></p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <form onSubmit={e => { e.preventDefault(); fetchData(q); }} style={{ flex: isMobile ? '1 1 100%' : 1 }}>
          <div className="search-bar">
            <Search size={14} className="search-icon" />
            <input className="input" placeholder="Search by name…"
              value={q} onChange={e => setQ(e.target.value)} />
          </div>
        </form>
        <button className="btn btn-ghost" onClick={() => fetchData(q)}><RefreshCw size={14} /></button>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setModal('create')} style={{ flex: isMobile ? 1 : undefined, justifyContent: isMobile ? 'center' : undefined }}>
            <Plus size={14} /> New Campaign
          </button>
        )}
      </div>

      {loading && data.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
        </div>
      ) : isMobile ? (
        data.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>No campaigns</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data.map(c => (
              <CampaignCard
                key={c.id || c._id}
                c={c}
                isAdmin={isAdmin}
                onEdit={() => setModal(c)}
                onEditFlow={() => navigate(`/campaigns/${c.id || c._id}/flow`)}
                onDownloadSpec={() => window.open(`${api.defaults.baseURL}/campaigns/${c.id || c._id}/spec/pdf`, '_blank')}
                onKeys={() => setKeysFor(c)}
                onPartnerMapping={() => setPartnerMappingFor(c)}
                onTapfiliate={() => setTapfiliateFor(c)}
                onDelete={() => setDelId(c.id || c._id)}
              />
            ))}
          </div>
        )
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Vertical</th>
                <th>Intake</th>
                <th>Affiliate</th>
                <th>Payout Type</th>
                <th>Status</th>
                {isAdmin && <th style={{ width: 130 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                  No campaigns
                </td></tr>
              ) : data.map(c => (
                <tr key={c.id || c._id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{c.name}</div>
                  </td>
                  <td>{c.vertical || '—'}</td>
                  <td><span className="badge badge-gray">{intakeTypeLabel(c.intake_type)}</span></td>
                  <td>{c.affiliate_profile?.name || c.affiliate_profile_id || '—'}</td>
                  <td>
                    <span className="badge badge-gray">{c.payout_type}</span>
                  </td>
                  <td>
                    <span className={`badge ${c.is_active !== false ? 'badge-green' : 'badge-red'}`}>
                      {c.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {isAdmin && (
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {c.intake_type === 'api' && (
                          <button className="btn btn-ghost btn-icon btn-sm" title="Download API Spec (PDF)" onClick={() => window.open(`${api.defaults.baseURL}/campaigns/${c.id || c._id}/spec/pdf`, '_blank')}>
                            <Download size={13} />
                          </button>
                        )}
                        <button className="btn btn-ghost btn-icon btn-sm" title="Edit Flow" onClick={() => navigate(`/campaigns/${c.id || c._id}/flow`)}>
                          <GitBranch size={13} />
                        </button>
                        <button className="btn btn-ghost btn-icon btn-sm" title="API Keys" onClick={() => setKeysFor(c)}>
                          <KeyRound size={13} />
                        </button>
                        {(c.intake_type === 'click' || c.intake_type === 'affiliate_clicks') && (
                          <button className="btn btn-ghost btn-icon btn-sm" title="Click Partner Mapping" onClick={() => setPartnerMappingFor(c)}>
                            <Link2 size={13} />
                          </button>
                        )}
                        {c.intake_type === 'affiliate_clicks' && (
                          <button className="btn btn-ghost btn-icon btn-sm" title="Tapfiliate Config" onClick={() => setTapfiliateFor(c)}>
                            <Share2 size={13} />
                          </button>
                        )}
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setModal(c)}>
                          <Pencil size={13} />
                        </button>
                        <button className="btn btn-danger btn-icon btn-sm"
                          onClick={() => setDelId(c.id || c._id)}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(modal === 'create' || (modal && (modal.id || modal._id))) && (
        <CampaignModal
          campaign={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); fetchData(q); }}
        />
      )}

      {keysFor && (
        <ApiKeysModal
          affiliateId={keysFor.affiliate_profile_id || keysFor.affiliate_profile?.id}
          campaignId={keysFor.id || keysFor._id}
          campaignName={keysFor.name}
          onClose={() => setKeysFor(null)}
        />
      )}

      {partnerMappingFor && (
        <ClickPartnerMappingModal
          campaignId={partnerMappingFor.id || partnerMappingFor._id}
          campaignName={partnerMappingFor.name}
          onClose={() => setPartnerMappingFor(null)}
        />
      )}

      {tapfiliateFor && (
        <TapfiliateConfigModal
          campaignId={tapfiliateFor.id || tapfiliateFor._id}
          campaignName={tapfiliateFor.name}
          affiliateName={tapfiliateFor.affiliate_profile?.name}
          onClose={() => setTapfiliateFor(null)}
        />
      )}

      {delId && (
        <div className="modal-overlay" onClick={() => setDelId(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header"><h2>Delete Campaign</h2></div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)' }}>Are you sure you want to delete this campaign?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={() => setDelId(null)}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(delId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
